class PupmdPackageV2 extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {IntersectionObserver | null} */
  #progressObserver = null;

  /** @type {number | null} */
  #stage2Timer = null;

  static #PROGRESS_STAGE2_DELAY_MS = 2000;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#bindPurchaseToggle(signal);
    this.#bindBundles(signal);
    this.#bindBundleRadios(signal);
    this.#bindProgressBar();
    this.#syncPurchasePanels();
    this.#updateSummary();
  }

  #syncPurchasePanels() {
    const activeToggle = this.querySelector('[data-purchase-toggle].is-active');
    const mode = activeToggle instanceof HTMLElement ? activeToggle.getAttribute('data-purchase-toggle') : null;
    const activeMode = mode === 'onetime' ? 'onetime' : 'subscribe';

    const subscribePanel = this.querySelector('[data-package-panel="subscribe"]');
    const onetimePanel = this.querySelector('[data-package-panel="onetime"]');

    if (subscribePanel instanceof HTMLElement) {
      subscribePanel.hidden = activeMode !== 'subscribe';
    }

    if (onetimePanel instanceof HTMLElement) {
      onetimePanel.hidden = activeMode !== 'onetime';
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#progressObserver?.disconnect();
    this.#progressObserver = null;

    if (this.#stage2Timer !== null) {
      window.clearTimeout(this.#stage2Timer);
      this.#stage2Timer = null;
    }
  }

  /** @param {AbortSignal} signal */
  #bindPurchaseToggle(signal) {
    this.querySelectorAll('[data-purchase-toggle]').forEach((toggleButton) => {
      if (!(toggleButton instanceof HTMLElement)) return;

      toggleButton.addEventListener(
        'click',
        () => {
          const mode = toggleButton.getAttribute('data-purchase-toggle');
          if (!mode) return;

          this.querySelectorAll('[data-purchase-toggle]').forEach((button) => {
            if (button instanceof HTMLElement) {
              button.classList.toggle('is-active', button === toggleButton);
            }
          });

          this.#syncPurchasePanels();

          const visiblePanel = this.querySelector(
            `[data-package-panel="${mode === 'onetime' ? 'onetime' : 'subscribe'}"]`,
          );
          const activeBundle = visiblePanel?.querySelector('[data-bundle-option].is-active');
          if (activeBundle instanceof HTMLElement) {
            this.#setActiveBundle(activeBundle);
          }

          this.#updateSummary();
        },
        { signal },
      );
    });
  }

  /** @param {AbortSignal} signal */
  #bindBundles(signal) {
    this.querySelectorAll('[data-bundle-option]').forEach((bundleOption) => {
      if (!(bundleOption instanceof HTMLElement)) return;

      bundleOption.addEventListener(
        'click',
        () => {
          this.#setActiveBundle(bundleOption);
          this.#updateSummary();
        },
        { signal },
      );
    });
  }

  /** @param {AbortSignal} signal */
  #bindBundleRadios(signal) {
    this.querySelectorAll('[data-bundle-option] input[type="radio"]').forEach((radioInput) => {
      if (!(radioInput instanceof HTMLInputElement)) return;

      radioInput.addEventListener(
        'change',
        () => {
          const bundleOption = radioInput.closest('[data-bundle-option]');
          if (!(bundleOption instanceof HTMLElement)) return;

          this.#setActiveBundle(bundleOption);
          this.#updateSummary();
        },
        { signal },
      );
    });
  }

  /** @param {HTMLElement} bundleOption */
  #setActiveBundle(bundleOption) {
    const panel = bundleOption.closest('[data-package-panel]');
    if (!panel) return;

    panel.querySelectorAll('[data-bundle-option]').forEach((bundleElement) => {
      if (bundleElement instanceof HTMLElement) {
        bundleElement.classList.toggle('is-active', bundleElement === bundleOption);
      }
    });

    const radioInput = bundleOption.querySelector('input[type="radio"]');
    if (radioInput instanceof HTMLInputElement) {
      radioInput.checked = true;
    }
  }

  #updateSummary() {
    const panel = this.#visiblePanel();
    const activeBundle = panel?.querySelector('[data-bundle-option].is-active');
    if (!(activeBundle instanceof HTMLElement)) return;

    const totalElement = this.querySelector('[data-package-total]');
    const saveWrapElement = this.querySelector('[data-package-save-wrap]');
    const saveAmountElement = this.querySelector('[data-package-save]');
    const comparePriceElement = this.querySelector('[data-package-cut]');
    const buyButtonElement = this.querySelector('[data-package-buy]');

    const totalPrice = activeBundle.getAttribute('data-total') || '';
    const saveAmount = activeBundle.getAttribute('data-save') || '';
    const comparePrice = activeBundle.getAttribute('data-cut') || '';
    const buttonUrl = activeBundle.getAttribute('data-button-url') || '#';

    if (totalElement instanceof HTMLElement) {
      totalElement.textContent = totalPrice;
    }

    if (comparePriceElement instanceof HTMLElement) {
      if (!comparePrice) {
        comparePriceElement.hidden = true;
      } else {
        comparePriceElement.hidden = false;
        comparePriceElement.textContent = comparePrice;
      }
    }

    if (saveWrapElement instanceof HTMLElement && saveAmountElement instanceof HTMLElement) {
      const hideSave =
        !saveAmount || saveAmount === '0' || saveAmount === '00' || saveAmount === '$0' || saveAmount === '$00';

      if (hideSave) {
        saveWrapElement.hidden = true;
      } else {
        saveWrapElement.hidden = false;
        saveAmountElement.textContent = saveAmount;
      }
    }

    if (buyButtonElement instanceof HTMLAnchorElement) {
      buyButtonElement.removeAttribute('data-pupmd-base-href');
      buyButtonElement.href = buttonUrl;

      const checkoutLinksElement = buyButtonElement.closest('pupmd-checkout-links-v2');
      if (
        checkoutLinksElement instanceof HTMLElement &&
        'refresh' in checkoutLinksElement &&
        typeof checkoutLinksElement.refresh === 'function'
      ) {
        checkoutLinksElement.refresh();
      }
    }
  }

  /** @returns {HTMLElement | null} */
  #visiblePanel() {
    const subscribePanel = this.querySelector('[data-package-panel="subscribe"]');
    if (subscribePanel instanceof HTMLElement && !subscribePanel.hidden) {
      return subscribePanel;
    }

    const onetimePanel = this.querySelector('[data-package-panel="onetime"]');
    if (onetimePanel instanceof HTMLElement && !onetimePanel.hidden) {
      return onetimePanel;
    }

    return null;
  }

  #bindProgressBar() {
    const progressElement = this.querySelector('[data-progress]');
    const progressFillElement = progressElement?.querySelector('[data-progress-fill]');
    if (!(progressElement instanceof HTMLElement) || !(progressFillElement instanceof HTMLElement)) {
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      progressElement.classList.add('is-in-view');
      progressFillElement.classList.add('is-stage-2');
      return;
    }

    this.#progressObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              progressElement.classList.add('is-in-view');

              this.#stage2Timer = window.setTimeout(() => {
                progressFillElement.classList.add('is-stage-2');
                this.#stage2Timer = null;
              }, PupmdPackageV2.#PROGRESS_STAGE2_DELAY_MS);
            });
          });

          this.#progressObserver?.disconnect();
          this.#progressObserver = null;
        });
      },
      { threshold: 0.25 },
    );

    this.#progressObserver.observe(progressElement);
  }
}

if (!customElements.get('pupmd-package-v2')) {
  customElements.define('pupmd-package-v2', PupmdPackageV2);
}
